import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { connect, ConnectedProps } from "react-redux";
import {
  $Button,
  $Form,
  $Row,
  $Col,
  $Select,
  $AutoComplete,
  $Collapse,
  $Tooltip,
  $Popconfirm,
  $Alert,
  $MessageBox,
} from "us.common/components";
import { RootState } from "us.helper/types";
import {
  CONJUNCTIONS,
  OPERATORS,
  RowAction,
} from "components/TemplateEditor/Constants";
import { DrawerType } from "constants/TemplateEditor";
import { PlusOutlined, DeleteOutlined } from "us.icons";
import {
  getRuleCondition,
  isRulesDuplicating,
  getRuleIndex,
} from "../../Functions";
import { generateUniqueKey } from "functions/TemplateEditor";
import _ from "lodash";
const { Panel } = $Collapse;

interface IEditorForm {
  defaultRule?: any;
  handleSubmit: () => void;
  onCancel: () => void;
}

const EditorForm: React.FC<IEditorForm & PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const {
    defaultRule,
    templateRules,
    parameters,
    drawerInfo,
    onCancel,
    handleSubmit,
  } = props;
  const { values, isValid, setFieldValue, setFieldTouched } =
    useFormikContext<any>();

  const ruleContainerRef = useRef<any>();

  const [activeCollapseKey, setActiveCollapseKey] = useState<Array<any>>([]);
  const { dataTokens } = parameters.standard ?? {};
  const rulesData = [...values?.rules];

  //filteredRules only used in section rules
  const filteredRules = useMemo(() => {
    return rulesData?.filter(({ isDeleted }: any) => !isDeleted);
  }, [rulesData]);

  /**
   * @Description Get indexes for collapse component
   */
  const rowKeys = useMemo(() => {
    return rulesData?.map(({ key }: any) => key);
  }, [rulesData]);

  /**
   * @Description Add new rule or condition
   */
  const handleAdd = (type: RowAction, key?: any) => {
    try {
      let newRules = rulesData;
      const ruleIndex = getRuleIndex(key, rulesData);

      const defaultFields = {
        leftOperand: undefined,
        operator: undefined,
        rightOperand: undefined,
        conjunction: undefined,
      };

      if (Array.isArray(newRules)) {
        if (
          typeof ruleIndex !== "undefined" &&
          type == RowAction.CONDITION &&
          newRules[ruleIndex]?.conditions?.length > 0
        ) {
          newRules[ruleIndex].conditions = [
            ...newRules[ruleIndex].conditions,
            defaultFields,
          ];
        }
        if (type == RowAction.RULE) {
          const defaultSectionRule = defaultRule
            ? { ...defaultRule, isDefault: false }
            : {};

          newRules?.push({
            ...defaultSectionRule,
            key: generateUniqueKey(),
            conditions: [defaultFields],
          });
          setActiveCollapseKey([
            ...activeCollapseKey,
            newRules[newRules?.length - 1]?.key,
          ]);
        }

        setFieldValue("rules", newRules);
      }
    } catch (e) {
      console.error("Rule add error: ", e);
    }
  };

  /**
   * @Description Delete new rule or condition
   */
  const handleDelete = (key: any, type: RowAction, conditionIndex?: number) => {
    try {
      let newRules = rulesData;
      const ruleIndex = getRuleIndex(key, rulesData);
      if (newRules?.length > 0) {
        if (
          type == RowAction.CONDITION &&
          newRules[ruleIndex].conditions?.length > 1
        ) {
          newRules[ruleIndex].conditions.splice(conditionIndex, 1);
        }
        if (type == RowAction.RULE) {
          if (
            drawerInfo.type == DrawerType.SECTION_RULES &&
            newRules[ruleIndex].id
          ) {
            setActiveCollapseKey(_.union(activeCollapseKey, [key]));
            newRules[ruleIndex].isDeleted = true;
            $MessageBox(
              "success",
              `US.USC.TEMPLATEEDITOR:MESSAGES.SECTION_RULE_DELETE_SUCCESS`,
              "",
              ""
            );
          } else {
            newRules.splice(ruleIndex, 1);
            $MessageBox(
              "success",
              `US.USC.TEMPLATEEDITOR:MESSAGES.TEMPLATE_RULE_DELETE_SUCCESS`,
              "",
              ""
            );
          }
        }
        setFieldValue("rules", newRules);
      }
    } catch (e) {
      console.error("Delete error: ", e);
      $MessageBox(
        "error",
        `US.USC.TEMPLATEEDITOR:MESSAGES.DELETE_FAIL`,
        "",
        ""
      );
    }
  };

  return (
    <div className="rule-editor-container" ref={ruleContainerRef}>
      {filteredRules?.length > 1 && (
        <div className="d-flex flex-fill justify-content-end">
          {rowKeys?.length !== activeCollapseKey?.length && (
            <$Button
              className="mt-n3 mr-n1"
              type="link"
              onClick={() => setActiveCollapseKey(rowKeys)}
            >
              {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.EXPAND_ALL")}
            </$Button>
          )}
          {rowKeys?.length == activeCollapseKey?.length && (
            <$Button
              className="mt-n3 mr-n1"
              type="link"
              onClick={() => setActiveCollapseKey([])}
            >
              {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.COLLAPSE_ALL")}
            </$Button>
          )}
        </div>
      )}
      <$Form layout="vertical" autoComplete="off">
        <div className="rule-collapse">
          <$Collapse
            bordered={false}
            activeKey={activeCollapseKey}
            onChange={(keys: any) => setActiveCollapseKey(keys)}
          >
            {rulesData?.map((rule: any) => {
              const ruleIndex = getRuleIndex(rule?.key, rulesData);
              return !rule?.isDeleted ? (
                <Panel
                  key={rule?.key}
                  header={
                    <div className="d-flex flex-fill justify-content-between">
                      <div className="counter-number" />
                      <span
                        id="rule-editor-text"
                        className="text-muted font-me"
                        style={{ padding: "9px 5px" }}
                      >
                        {getRuleCondition(rulesData, rule?.key, dataTokens)}
                      </span>
                      <div
                        className="d-flex"
                        style={{ minHeight: "40px" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <$Popconfirm
                          title={t("COMMON.SURE_TO_DELETE_?")}
                          placement="topLeft"
                          onConfirm={() => {
                            handleDelete(rule?.key, RowAction.RULE);
                          }}
                        >
                          <div className="rule-delete-btn">
                            <DeleteOutlined className="text-error" />
                          </div>
                        </$Popconfirm>
                        {filteredRules[filteredRules?.length - 1]?.key ==
                          rule?.key && (
                          <$Tooltip
                            placement="topLeft"
                            title={t(
                              "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADD_NEW_RULE"
                            )}
                          >
                            <div
                              className="add-btn"
                              onClick={() =>
                                handleAdd(RowAction.RULE, rule?.key)
                              }
                            >
                              <PlusOutlined />
                            </div>
                          </$Tooltip>
                        )}
                      </div>
                    </div>
                  }
                >
                  {rule?.conditions?.map(
                    (condition: any, conditionIndex: number) => (
                      <div className="rule-item-container" key={conditionIndex}>
                        <div className="rule-content-items">
                          <$Row
                            gutter={20}
                            className="align-content-center w-100"
                          >
                            <$Col span={7}>
                              <$Select
                                name={`rules[${ruleIndex}].conditions[${conditionIndex}].leftOperand`}
                                options={dataTokens}
                                optionValue="tokenValue"
                                optionText="tokenName"
                                size="small"
                                allOption={false}
                                formitem={{}}
                                placeholder={t(
                                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.LEFT_OPERAND"
                                )}
                                onClick={() =>
                                  setFieldTouched(
                                    `rules[${ruleIndex}].conditions[${conditionIndex}].leftOperand`,
                                    true,
                                    false
                                  )
                                }
                                getPopupContainer={() =>
                                  ruleContainerRef.current
                                }
                                defaultActiveFirstOption
                                required
                                loading={parameters.isLoading}
                                onSearchBy={["tokenName", "tokenValue"]}
                              />
                            </$Col>
                            <$Col span={5}>
                              <$Select
                                name={`rules[${ruleIndex}].conditions[${conditionIndex}].operator`}
                                options={OPERATORS}
                                optionValue="value"
                                optionText="label"
                                size="small"
                                allOption={false}
                                formitem={{}}
                                placeholder={t(
                                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.OPERATOR"
                                )}
                                onClick={() =>
                                  setFieldTouched(
                                    `rules[${ruleIndex}].conditions[${conditionIndex}].operator`,
                                    true,
                                    false
                                  )
                                }
                                getPopupContainer={() =>
                                  ruleContainerRef.current
                                }
                                defaultActiveFirstOption
                                required
                              />
                            </$Col>
                            <$Col span={7}>
                              <$AutoComplete
                                name={`rules[${ruleIndex}].conditions[${conditionIndex}].rightOperand`}
                                placeholder={t(
                                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.RIGHT_OPERAND"
                                )}
                                options={dataTokens.map(
                                  ({ tokenName, tokenValue }: any) => ({
                                    label: tokenName,
                                    value: tokenValue,
                                  })
                                )}
                                size="small"
                                virtual={false}
                              />
                            </$Col>
                            <$Col span={5}>
                              <$Select
                                name={`rules[${ruleIndex}].conditions[${conditionIndex}].conjunction`}
                                options={CONJUNCTIONS}
                                size="small"
                                formitem={{}}
                                placeholder={t(
                                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.CONJUNCTION"
                                )}
                                allOption={false}
                                optionValue="value"
                                optionText="label"
                                defaultActiveFirstOption
                                getPopupContainer={() =>
                                  ruleContainerRef.current
                                }
                                required
                                onClick={() =>
                                  setFieldTouched(
                                    `rules[${ruleIndex}].conditions[${conditionIndex}].conjunction`,
                                    true,
                                    false
                                  )
                                }
                              />
                            </$Col>
                          </$Row>
                        </div>
                        <div
                          className="d-flex align-items-center justify-content-end"
                          style={{ width: "90px" }}
                        >
                          <div className="d-flex h-100 align-items-center">
                            <$Popconfirm
                              title={t("COMMON.SURE_TO_DELETE_?")}
                              placement="topLeft"
                              onConfirm={() => {
                                handleDelete(
                                  rule?.key,
                                  RowAction.CONDITION,
                                  conditionIndex
                                );
                              }}
                            >
                              <div
                                className="rule-delete-btn"
                                hidden={rule?.conditions?.length == 1}
                              >
                                <DeleteOutlined className="text-error" />
                              </div>
                            </$Popconfirm>
                            {rule?.conditions?.length - 1 == conditionIndex && (
                              <$Tooltip
                                placement="topLeft"
                                title={t(
                                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADD_NEW_CONDITION"
                                )}
                              >
                                <div
                                  className="add-btn condition"
                                  onClick={() =>
                                    handleAdd(RowAction.CONDITION, rule?.key)
                                  }
                                >
                                  <PlusOutlined className="add-btn-text" />
                                </div>
                              </$Tooltip>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </Panel>
              ) : (
                <></>
              );
            })}
          </$Collapse>
          {isRulesDuplicating(filteredRules, dataTokens) && (
            <$Alert
              message={t(
                "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.THERE_ARE_DUPLICATE_ENTRIES"
              )}
              type="error"
              showIcon
              className="error-message mt-3"
            />
          )}
          {filteredRules?.length == 0 && (
            <$Button
              type="primary"
              size="small"
              className="w-100"
              onClick={() => handleAdd(RowAction.RULE)}
              icon={<PlusOutlined />}
            >
              {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADD_NEW_RULE")}
            </$Button>
          )}
        </div>
      </$Form>
      <div className="drawer-footer-fixed align-content-center justify-content-end">
        <div>
          <$Button
            className="mr-2"
            onClick={handleSubmit}
            type="primary"
            loading={templateRules.isSaving}
            disabled={
              !isValid ||
              templateRules.isSaving ||
              dataTokens.length == 0 ||
              isRulesDuplicating(filteredRules, dataTokens)
            }
          >
            {t("US.COLLECTION.COMMON:COMMON.SAVE")}
          </$Button>
          <$Button onClick={() => onCancel()}>
            {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
          </$Button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templateEditor } = state;
  const { parameters, templateRules, drawerInfo } = templateEditor;

  return {
    drawerInfo,
    parameters,
    templateRules,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(EditorForm);
